import { useEffect, useRef } from "react";
import { usePageVisibility } from "./usePageVisibility";

interface UseAutoMarkReadOptions<T> {
  messages: T[];
  isAtBottom: boolean;
  isLoading: boolean;
  markMessagesAsRead: (messages: T[]) => void;
  enabled?: boolean;
}

/**
 * Hook to automatically mark messages as read based on visibility and scroll position.
 *
 * Business Logic:
 * 1. If the user is looking at the screen (visible) AND scrolled to the bottom:
 *    - Automatically mark all messages as read.
 * 2. If new messages arrive while meeting condition #1:
 *    - Automatically mark them as read.
 * 3. If the user returns to the tab (visibility becomes true):
 *    - Automatically mark messages as read if unread messages exist.
 */
export function useAutoMarkRead<T>({
  messages,
  isAtBottom,
  isLoading,
  markMessagesAsRead,
  enabled = true,
}: UseAutoMarkReadOptions<T>) {
  const isVisible = usePageVisibility();

  // Ref to track if we have attempted to mark the current set of messages
  // This prevents spamming the API for the same message list
  const lastMarkedMessagesLengthRef = useRef<number>(0);
  const hasMarkedInitialReadRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || isLoading || messages.length === 0) return;

    // 1. Initial Load Force Check
    // When messages are first loaded, we assume the user intends to read them.
    // We trigger markAsRead regardless of visibility state to be safe (if component is mounted, user is likely there)
    if (!hasMarkedInitialReadRef.current) {
      console.log("[useAutoMarkRead] 🚀 Initial Force Read Triggered", {
        msgCount: messages.length,
      });
      markMessagesAsRead(messages);
      lastMarkedMessagesLengthRef.current = messages.length;
      hasMarkedInitialReadRef.current = true;
      return;
    }

    // 2. Standard Logic (Scroll & New Messages)
    // Logic:
    // If visible AND at bottom, we check if we should mark read.
    // We trigger if:
    //    a) New messages arrived (length increased)
    //    b) We just reached the bottom (isAtBottom became true)
    //    c) We just became visible (isVisible became true)
    if (isVisible && isAtBottom) {
      markMessagesAsRead(messages);
      lastMarkedMessagesLengthRef.current = messages.length;
    }
  }, [
    messages.length,
    isAtBottom,
    isLoading,
    isVisible,
    markMessagesAsRead,
    enabled,
  ]);

  // Reset initial read flag if roomId changes (implied by messages array reset usually,
  // but if messages array identity changes significantly we might want to reset.
  // Actually, standard usage relies on mounting a new hook instance for new room.
  // So refs are reset automatically.)
}
